import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyState from "global/components/EmptyState/EmptyState";
import notifiers from "global/constants/NotificationConstants";
import { openErrorNotification, shortenFileName } from "helpers/methods";
import { SignerAttachment } from "models/interfaces";
import {
  downloadAllRequestAttachments,
  downloadRequestAttachment,
  fetchRequestAttachments,
} from "../MySignRequestService";
import viewSignRequestStyles from "./ViewSignRequest.styles";
import viewSignRequestTokens from "./ViewSignRequest.tokens";
import {
  ChevronDownGlyph,
  DocumentGlyph,
  DownloadGlyph,
} from "./ViewRequestIcons";

const styles = viewSignRequestStyles;
const { documentRow } = viewSignRequestTokens;

interface AttachmentsCardProps {
  requestId: string;
  /** Who uploaded what — shown per row so the sender can tell the signers apart. */
  signerNameByEmail?: Record<string, string>;
}

const formatSize = (bytes: number) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Hands a fetched blob to the browser as a download. */
const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const AttachmentsCard: React.FC<AttachmentsCardProps> = ({
  requestId,
  signerNameByEmail,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [attachments, setAttachments] = useState<SignerAttachment[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!requestId) return undefined;
    (async () => {
      try {
        const response = await fetchRequestAttachments(requestId);
        if (!cancelled) setAttachments(response);
      } catch (error: any) {
        // No toast. A 403 here just means the account's plan lacks the feature, and an envelope with no
        // attachment fields is the common case — neither deserves an error toast on the view screen.
        // Anything else (a 404 from a moved endpoint, a 500) is a real failure that used to vanish
        // here without a trace, so it at least goes to the console.
        if (error?.statusCode !== 403) {
          console.error("Failed to load request attachments", error);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  const handleDownload = async (attachment: SignerAttachment) => {
    setDownloadingId(attachment.id);
    try {
      saveBlob(
        await downloadRequestAttachment(attachment.id),
        attachment.fileName,
      );
    } catch {
      openErrorNotification(notifiers.ATTACHMENT_DOWNLOAD_FAILED);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      saveBlob(
        await downloadAllRequestAttachments(requestId),
        "attachments.zip",
      );
    } catch {
      openErrorNotification(notifiers.ATTACHMENT_DOWNLOAD_FAILED);
    } finally {
      setDownloadingAll(false);
    }
  };

  if (!loaded || attachments.length === 0) return null;

  const uploaderLabel = (attachment: SignerAttachment) => {
    const email = attachment.signerEmail || "";
    const name = signerNameByEmail?.[email.toLowerCase()];
    return name || email;
  };

  return (
    <Box sx={styles.card}>
      <Box sx={styles.sectionHeader(false)}>
        <Box sx={styles.sectionHeaderLead}>
          <Box component="h2" sx={styles.sectionTitle}>
            Attachments
          </Box>
          <Box component="span" sx={styles.sectionCount()}>
            {attachments.length}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            disableRipple
            disabled={downloadingAll}
            onClick={handleDownloadAll}
            aria-label="Download all attachments"
            sx={styles.documentAction}
          >
            <DownloadGlyph
              sx={{
                width: documentRow.actionIconSize,
                height: documentRow.actionIconSize,
              }}
            />
            Download all
          </Button>
          <IconButton
            disableRipple
            onClick={() => setExpanded((value) => !value)}
            aria-label={expanded ? "Collapse attachments" : "Expand attachments"}
            sx={styles.sectionToggle(expanded)}
          >
            <ChevronDownGlyph sx={styles.sectionToggleGlyph} />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {attachments.length === 0 ? (
          <EmptyState
            size="medium"
            icon="doc"
            title="No attachments on this request"
          />
        ) : (
          attachments.map((attachment) => {
            const meta = [formatSize(attachment.sizeBytes), uploaderLabel(attachment)]
              .filter(Boolean)
              .join(" · ");
            return (
              <Box key={attachment.id} sx={styles.documentRow}>
                <Box sx={styles.documentThumb}>
                  <DocumentGlyph sx={styles.documentThumbGlyph} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Middle-trimmed so the extension stays readable — the CSS ellipsis in
                      styles.documentName truncates the tail, which on a very long name leaves an
                      unreadable run with no clue whether it is the PDF or the JPG. Tooltip still gives
                      the full name, and the ellipsis remains as the backstop on a narrow viewport. */}
                  <Tooltip title={attachment.fileName} arrow describeChild>
                    <Typography component="div" sx={styles.documentName}>
                      {shortenFileName(attachment.fileName, 60)}
                    </Typography>
                  </Tooltip>
                  <Typography component="div" sx={styles.documentMeta}>
                    {meta}
                  </Typography>
                </Box>
                <IconButton
                  disableRipple
                  disabled={downloadingId === attachment.id}
                  onClick={() => handleDownload(attachment)}
                  aria-label={`Download ${attachment.fileName}`}
                  sx={styles.documentIconAction}
                >
                  <DownloadGlyph sx={styles.documentIconGlyph} />
                </IconButton>
              </Box>
            );
          })
        )}
      </Collapse>
    </Box>
  );
};

export default AttachmentsCard;
